/**
 * @file Route error fallback (1.13.6, F1.9).
 *
 * React Router renders this, inside the shell, when a route throws or its
 * lazy module fails to load. Without it the visitor sees React Router's own
 * development screen. The failure is logged for operators, never shown, and
 * handed to the client error reporter (`lib/observability`) so it can be
 * matched to the API's logs by X-Request-Id. Reporting happens in an effect
 * rather than during render, so a reporting problem can never become a second
 * render failure, and the reporter de-duplicates the repeat StrictMode causes
 * in development.
 *
 * The storefront's fallback (`ErrorFallback.tsx` there) is the model. It is
 * not a copy, because it is built from storefront components this app does not
 * carry; the reporting rule is the same, including that a thrown 404 is a
 * routing outcome and not a defect, so it is not reported.
 */
import { useEffect } from 'react';
import { isRouteErrorResponse, useLocation, useRouteError } from 'react-router';

import { MAIN_ID } from '../../app/layout/AppShell';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { Stack } from '../../components/ui/Stack';
import { useDocumentTitle } from '../../lib/a11y/useDocumentTitle';
import { reloadPage } from '../../lib/navigation';
import { reportClientError } from '../../lib/observability/reporter';
import styles from '../verify/verify.module.css';

import type { ReactElement } from 'react';

/** The fallback. */
export function ErrorFallback(): ReactElement {
  const error = useRouteError();
  const { pathname } = useLocation();
  useDocumentTitle('Something went wrong');
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;
  useEffect(() => {
    if (isNotFound) return;
    console.error('Route error', error);
    reportClientError({ kind: 'route_error', error, pathname });
  }, [error, isNotFound, pathname]);
  return (
    <main id={MAIN_ID}>
      <Container width="narrow">
        <Stack gap={6}>
          {/* A div, because a header may not take the alert role. */}
          <div className={styles.header} role="alert">
            <Eyebrow>Early access</Eyebrow>
            <h1>Something went wrong</h1>
            <p>This page did not load properly. Nothing you have already bought is affected.</p>
          </div>
          <div>
            <Button type="button" onClick={reloadPage}>
              Try again
            </Button>
          </div>
        </Stack>
      </Container>
    </main>
  );
}
